import { memo, useEffect, useRef, useState } from 'react';

import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Fade from '@mui/material/Fade';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import CheckIcon from '@mui/icons-material/Check';
import ErrorOutlinedIcon from '@mui/icons-material/ErrorOutlined';
import SaveIcon from '@mui/icons-material/Save';

import { useApp, SAVE_STATUSES } from '../../contextProviders/AppProvider';
import { SaveStatus } from '../../types/SaveStatus';

interface SaveStatusIndicatorProps {
  size?: number;
  minSavingDuration?: number; // Minimum time to show saving state (ms)
}

const SaveStatusIndicator = memo(({ size = 24, minSavingDuration = 600 }: SaveStatusIndicatorProps) => {
  const { saveStatus } = useApp();
  const [displayStatus, setDisplayStatus] = useState<SaveStatus>(SAVE_STATUSES.IDLE);
  // Not React state: only ever read inside the setTimeout callback below, never rendered. Making
  // it state (and a dependency of this same effect) previously caused an infinite loop — the
  // SAVING branch unconditionally set a fresh Date.now() every run, which retriggered the effect
  // (savingStartTime changed), which set another fresh Date.now(), forever, for as long as
  // saveStatus stayed SAVING (i.e. any real save that took more than one render tick).
  const savingStartTimeRef = useRef<number | null>(null);

  useEffect(() => {
    if (saveStatus === SAVE_STATUSES.SAVING) {
      // Start saving - record the time and show saving immediately
      savingStartTimeRef.current = Date.now();
      setDisplayStatus(SAVE_STATUSES.SAVING);
    } else if (saveStatus === SAVE_STATUSES.SAVED || saveStatus === SAVE_STATUSES.ERROR) {
      // Transitioning from saving - ensure minimum duration
      if (savingStartTimeRef.current) {
        const elapsed = Date.now() - savingStartTimeRef.current;
        const remaining = minSavingDuration - elapsed;

        if (remaining > 0) {
          // Wait for remaining time before showing saved/error
          const timer = setTimeout(() => {
            setDisplayStatus(saveStatus);
            savingStartTimeRef.current = null;
          }, remaining);
          return () => clearTimeout(timer);
        }
      }
      // Minimum time has passed or wasn't saving
      setDisplayStatus(saveStatus);
      savingStartTimeRef.current = null;
    } else {
      // idle
      setDisplayStatus(SAVE_STATUSES.IDLE);
      savingStartTimeRef.current = null;
    }
  }, [saveStatus, minSavingDuration]);

  if (displayStatus === SAVE_STATUSES.IDLE) {
    return null;
  }

  const transitionDuration = 400;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        ml: 1,
        position: 'relative',
        height: size,
        minWidth: size,
      }}
    >
      {/* Icon container - fixed position for smooth crossfade */}
      <Box
        sx={{
          position: 'relative',
          width: size,
          height: size,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {/* Saving state: spinning progress with save icon */}
        <Fade
          in={displayStatus === SAVE_STATUSES.SAVING}
          timeout={{ enter: transitionDuration, exit: transitionDuration }}
        >
          <Box
            sx={{
              position: 'absolute',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: size,
              height: size,
            }}
          >
            <CircularProgress
              size={size}
              thickness={4}
              sx={{ color: 'primary.main' }}
            />
            <SaveIcon
              sx={{
                position: 'absolute',
                fontSize: size * 0.5,
                color: 'primary.main',
              }}
            />
          </Box>
        </Fade>

        {/* Saved state: checkmark */}
        <Fade
          in={displayStatus === SAVE_STATUSES.SAVED}
          timeout={{ enter: transitionDuration, exit: transitionDuration }}
        >
          <Box
            sx={{
              position: 'absolute',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CheckIcon
              sx={{
                fontSize: size,
                color: 'success.main',
              }}
            />
          </Box>
        </Fade>

        {/* Error state: error icon */}
        <Fade
          in={displayStatus === SAVE_STATUSES.ERROR}
          timeout={{ enter: transitionDuration, exit: transitionDuration }}
        >
          <Box
            sx={{
              position: 'absolute',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Tooltip title="Save failed. Changes may not be saved.">
              <ErrorOutlinedIcon
                sx={{
                  fontSize: size,
                  color: 'error.main',
                }}
              />
            </Tooltip>
          </Box>
        </Fade>
      </Box>

      {/* "Saved" text - fades in separately */}
      <Fade
        in={displayStatus === SAVE_STATUSES.SAVED}
        timeout={{ enter: transitionDuration + 100, exit: transitionDuration }}
      >
        <Typography
          variant="labelMedium"
          sx={{
            color: 'success.main',
            ml: 0.5,
            whiteSpace: 'nowrap',
          }}
        >
          Saved
        </Typography>
      </Fade>
    </Box>
  );
});

SaveStatusIndicator.displayName = 'SaveStatusIndicator';

export default SaveStatusIndicator;
